// shared/src/prompts/_news-render.ts
// Phase 2 of news-coverage-design-2026-05-24-brief-revision.md — shared
// NEWS-block renderer. Consumed by every prompt that wants per-ticker
// or per-topic news in its user message (p-analyst-v1, p-skeptic-v1,
// p-analyst-v2, p-skeptic-v2, p-arbiter, plus Phase 4 m-stages).
//
// Input is the LLM-facing shape from shared/src/news/types.ts —
// `tone`, `cross_vendor_hit`, `source_vendor`, `url` are NEVER passed
// here (server-side ranker projects them out before this function is
// called). See feedback_no_numeric_anchors_to_llm.md.

import type { LlmNewsItem } from '../news/types.js';

/** Render a numbered NEWS block. Items are assumed pre-ranked
 *  (cross-vendor hits + tier-A first). Empty input returns the
 *  '(no news in lookback window)' sentinel so the LLM has an
 *  explicit signal that the absence is real and not an error.
 *
 *  Summary text is clipped to 200 chars to keep the prompt budget
 *  predictable. Title is never clipped (load-bearing for LLM cite). */
export function renderNewsBlock(items: ReadonlyArray<LlmNewsItem>): string {
  if (items.length === 0) return '  (no news in lookback window)';
  return items.map((it, i) => {
    const tier   = `[${it.source_tier}]`;
    const date   = it.published_at_iso.slice(0, 16).replace('T', ' ');
    const summary = it.summary
      ? ` — ${it.summary.length > 200 ? it.summary.slice(0, 200) + '...' : it.summary}`
      : '';
    return `  [${i + 1}] ${tier} ${it.source_domain} (${date}): ${it.title}${summary}`;
  }).join('\n');
}

/** Canonical SYSTEM-prompt section explaining the NEWS block. Each
 *  prompt that consumes news appends this verbatim to its SYSTEM
 *  so the rules stay consistent across stages. Per the design doc:
 *  tier categorical labels (A/B/C) are safe for LLM consumption; we
 *  never surface tone scalars or cross_vendor_hit boolean flags. */
export const NEWS_BLOCK_SYSTEM_DOCTRINE = `## NEWS BLOCK (Phase 2 — Gap A: ticker news enrichment)
The user message includes a NEWS block with up to 8 recent (last 48h) ticker-relevant articles, server-side deduped + ranked by source tier and recency. Each item is prefixed with [tier]:
- Tier A = Reuters, Bloomberg, WSJ, FT, CNBC, AP, Barron's, MarketWatch (weight highest)
- Tier B = SeekingAlpha, Yahoo, CNN, Forbes, Benzinga, BusinessWire, etc.
- Tier C = blogs, aggregators, low-signal sources (weight lowest)

Rules:
- Cite specific headlines in /reasoning when news MATERIALLY supports or contradicts your call. Format: "[N] <domain> reports <one-line summary> — <how it affects the call>". The bracketed index helps audit.
- Do NOT infer facts beyond what the headline + summary state. The headline is evidence; embellishment is hallucination.
- Recency matters: news within 24h is fresh signal; older items are staler.
- Empty NEWS block ("(no news in lookback window)") is a real signal — proceed without news context. Do NOT fabricate.
- News is one input among many. It does NOT override technicals, calibration, or β/γ when those structurally disagree; cite the conflict explicitly if it arises.
`;
